import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { ClientSidebar } from "@/components/sidebars";
import AnuraScanModal from "@/components/AnuraScanModal";
import { useAuth } from "@/lib/auth";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Salud() {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [activating, setActivating] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        apiClient.get("/anura/status"),
        apiClient.get("/anura/scans"),
      ]);
      setStatus(s.data);
      setScans(l.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const handleActivate = async () => {
    setActivating(true);
    try {
      await apiClient.post("/anura/addon/activate");
      toast.success("Add-on de Chequeo de Salud activado");
      await loadAll();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setActivating(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("¿Cancelar el add-on? Conservarás los escaneos ya realizados pero no podrás hacer nuevos.")) return;
    try {
      await apiClient.post("/anura/addon/cancel");
      toast.success("Add-on cancelado");
      await loadAll();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  if (loading || !status) {
    return <AppLayout sidebar={ClientSidebar}><div className="panel">Cargando…</div></AppLayout>;
  }

  const isActive = status.addon_active;
  const planLabel = status.plan === "estandar" ? "Plan Estándar" : status.plan === "basico" ? "Plan Básico" : "Sin plan";
  const beneficiarios = user?.beneficiarios || [];
  const noPlan = !status.plan || user?.membership_status !== "active";

  return (
    <AppLayout sidebar={ClientSidebar}>
      <header className="app-header">
        <h1><i className="fas fa-heart-pulse" style={{ color: "#DC2626", marginRight: 12 }} />Chequeo de Salud</h1>
        <p className="text-muted">
          Tecnología <strong>NuraLogix Anura</strong> · mide signos vitales sin contacto, sólo con la cámara del celular.
        </p>
      </header>

      {/* === Banner: aún no tiene membresía activa === */}
      {noPlan && (
        <div className="alert-card" data-testid="anura-need-plan">
          <i className="fas fa-info-circle" />
          <div>
            <strong>Activa primero tu membresía.</strong>
            <p>El módulo de Chequeo de Salud es un add-on que requiere un plan Básico o Estándar activo.</p>
          </div>
          <a className="btn btn-primary" href="/cuenta/plan">Ver planes</a>
        </div>
      )}

      {/* === Estado del add-on === */}
      <section className="cards-row">
        <div className={`stat-card ${isActive ? "stat-ok" : ""}`} data-testid="anura-stat-status">
          <span className="stat-label">Estado del add-on</span>
          <strong className="stat-value">{isActive ? "Activo" : "Inactivo"}</strong>
          <small>{planLabel}</small>
        </div>
        <div className="stat-card" data-testid="anura-stat-quota">
          <span className="stat-label">Escaneos disponibles</span>
          <strong className="stat-value">
            {status.scans_remaining}<small style={{ fontSize: 14, color: "#9ca3af" }}>/{status.quota_total || "—"}</small>
          </strong>
          <small>al año</small>
        </div>
        <div className="stat-card" data-testid="anura-stat-used">
          <span className="stat-label">Realizados</span>
          <strong className="stat-value">{status.scans_used}</strong>
          <small>escaneos este periodo</small>
        </div>
        <div className="stat-card" data-testid="anura-stat-renewal">
          <span className="stat-label">Renueva el</span>
          <strong className="stat-value" style={{ fontSize: 18 }}>
            {status.period_ends_at ? new Date(status.period_ends_at).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" }) : "—"}
          </strong>
          <small>vuelve a 0 escaneos</small>
        </div>
      </section>

      {/* === Tarjeta de acción principal === */}
      {!isActive ? (
        <div className="panel anura-cta-panel" data-testid="anura-cta">
          <div style={{ flex: 1 }}>
            <h2 style={{ marginTop: 0 }}>Agrega Chequeo de Salud a tu membresía</h2>
            <ul className="anura-feature-list">
              <li><i className="fas fa-check" /> Monitoreo de signos vitales en 30 segundos</li>
              <li><i className="fas fa-check" /> Detección temprana de hipertensión, riesgo cardíaco y estrés</li>
              <li><i className="fas fa-check" /> Historial guardado por beneficiario</li>
              <li><i className="fas fa-check" /> Tecnología <strong>FDA cleared</strong> (NuraLogix Anura)</li>
            </ul>
            <div className="anura-pricing">
              <div className={`anura-price-tag ${status.plan === "basico" ? "active" : ""}`}>
                <span>Plan Básico</span>
                <strong>+${status.pricing.basico_usd_month}<small>/mes</small></strong>
                <small>{status.pricing.quota_basico} escaneos/año</small>
              </div>
              <div className={`anura-price-tag ${status.plan === "estandar" ? "active" : ""}`}>
                <span>Plan Estándar</span>
                <strong>+${status.pricing.estandar_usd_month}<small>/mes</small></strong>
                <small>{status.pricing.quota_estandar} escaneos/año</small>
              </div>
            </div>
            <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 10 }}>
              💡 Versión beta — actualmente se activa sin cargo adicional para que pruebes el flujo.
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={handleActivate}
            disabled={activating || noPlan}
            data-testid="anura-activate-btn"
            style={{ alignSelf: "flex-start" }}
          >
            {activating ? "Activando…" : <><i className="fas fa-plus" /> Activar add-on</>}
          </button>
        </div>
      ) : (
        <div className="panel anura-active-panel" data-testid="anura-active-panel">
          <div style={{ flex: 1 }}>
            <h2 style={{ marginTop: 0, color: "#15803D" }}>
              <i className="fas fa-check-circle" /> Tu add-on está activo
            </h2>
            <p className="text-muted" style={{ margin: "4px 0 16px" }}>
              Realiza un chequeo a cualquiera de tus beneficiarios en menos de un minuto.
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                className="btn btn-primary"
                onClick={() => setShowModal(true)}
                disabled={beneficiarios.length === 0 || status.scans_remaining <= 0}
                data-testid="anura-scan-btn"
              >
                <i className="fas fa-camera" /> Iniciar nuevo chequeo
              </button>
              <button className="btn btn-secondary" onClick={handleCancel} data-testid="anura-cancel-btn">
                Cancelar add-on
              </button>
            </div>
            {beneficiarios.length === 0 && (
              <p style={{ color: "#B91C1C", marginTop: 12, fontSize: 13 }}>
                ⚠️ Agrega al menos un beneficiario para iniciar un chequeo.
              </p>
            )}
            {status.scans_remaining <= 0 && (
              <p style={{ color: "#B91C1C", marginTop: 12, fontSize: 13 }}>
                ⚠️ Agotaste tu cuota anual. Se renueva el {new Date(status.period_ends_at).toLocaleDateString("es-MX")}.
              </p>
            )}
          </div>
        </div>
      )}

      {/* === Historial === */}
      <div className="panel">
        <h2>Historial de chequeos</h2>
        {scans.length === 0 ? (
          <p className="text-muted">Aún no has realizado ningún chequeo.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Beneficiario</th>
                <th>Vitales</th>
                <th>Estrés</th>
                <th>Bienestar</th>
                <th>Riesgo principal</th>
              </tr>
            </thead>
            <tbody>
              {scans.map((s) => {
                const v = s.results.vitals;
                const risks = Object.entries(s.results.risks);
                const topRisk = risks.find(([, l]) => l === "alto") || risks.find(([, l]) => l === "medio");
                return (
                  <tr key={s.id} data-testid={`scan-row-${s.id}`}>
                    <td><small>{new Date(s.created_at).toLocaleString("es-MX")}</small></td>
                    <td><strong>{s.beneficiario_snapshot.nombre}</strong></td>
                    <td>
                      <small>
                        ❤️ {v.heart_rate_bpm} bpm · 🩸 {v.blood_pressure_systolic}/{v.blood_pressure_diastolic}
                      </small>
                    </td>
                    <td>{v.stress_index}/100</td>
                    <td><strong style={{ color: v.wellness_score >= 75 ? "#15803D" : v.wellness_score >= 50 ? "#B45309" : "#B91C1C" }}>{v.wellness_score}</strong></td>
                    <td>
                      {topRisk ? (
                        <span className={`sol-pill ${topRisk[1] === "alto" ? "sol-cancelada" : "sol-pendiente"}`}>
                          {topRisk[0].replace("_", " ")} · {topRisk[1]}
                        </span>
                      ) : <small style={{ color: "#9ca3af" }}>—</small>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <AnuraScanModal
        open={showModal}
        beneficiarios={beneficiarios}
        onClose={() => { setShowModal(false); loadAll(); }}
        onCompleted={() => { /* loadAll happens onClose */ }}
      />
    </AppLayout>
  );
}
